import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, X, Save, Loader2, Receipt } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';

const BASE = '/notes/tag-workspace';
let namesCache = null;
async function loadNames(force) {
  if (!namesCache || force) namesCache = (await notesApi.get(`${BASE}/charge-names`)).data || [];
  return namesCache;
}

// Rows → API body (blank names dropped; blank amount = to be filled at price time).
export const chargesBody = (rows) => rows
  .filter((r) => (r.name || '').trim())
  .map((r) => ({ name: r.name.trim(), amount: r.amount === '' || r.amount == null ? null : Math.round(Number(r.amount)) }));
export const chargeRows = (charges) => (charges || []).map((c) => ({ name: c.name, amount: c.amount == null ? '' : String(c.amount) }));

// Editable rows: name (suggestions incl. names typed before) + optional amount.
export function ChargesEditor({ rows, onChange, inputClass = 'h-8 rounded-lg border border-input bg-background px-2 text-sm' }) {
  const [names, setNames] = useState(namesCache || []);
  useEffect(() => { loadNames().then(setNames).catch(() => {}); }, []);
  const set = (i, k, v) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid="tagws-extra-charges-editor">
      <datalist id="tagws-charge-names">{names.map((n) => <option key={n} value={n} />)}</datalist>
      {rows.length === 0 ? <div className="rounded-lg border border-dashed border-border p-2.5 text-center text-xs text-muted-foreground">No extra charges.</div> : null}
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,1fr)_7.5rem_2rem] items-center gap-2" data-testid="tagws-extra-charge-row">
          <input value={r.name} onChange={(e) => set(i, 'name', e.target.value)} list="tagws-charge-names" maxLength={60} placeholder="Charge (e.g. Pohai)" className={`${inputClass} w-full min-w-0`} data-testid="tagws-extra-charge-name-input" />
          <input type="number" min="0" step="1" value={r.amount} onChange={(e) => set(i, 'amount', e.target.value)} placeholder="₹ Amount" className={`${inputClass} w-full min-w-0 font-mono`} data-testid="tagws-extra-charge-amount-input" />
          <button type="button" onClick={() => onChange(rows.filter((_, idx) => idx !== i))} aria-label="Remove charge" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-destructive" data-testid="tagws-extra-charge-remove-button"><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rows, { name: '', amount: '' }])} className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1 text-xs text-foreground hover:bg-secondary" data-testid="tagws-extra-charge-add-button"><Plus size={13} /> Add charge</button>
    </div>
  );
}

// Card icon: green when the piece has extra charges, grey otherwise.
export function ChargesIconButton({ item, onClick }) {
  const n = (item.extra_charges || []).length;
  return (
    <button type="button" onClick={() => onClick?.(item)} title={n ? `Extra charges: ${item.extra_charges.map((c) => c.name).join(', ')}` : 'Add extra charges'} aria-label="Extra charges"
      className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg border transition-colors duration-150 ${n ? 'border-emerald-300 bg-emerald-50 text-emerald-600 hover:bg-emerald-100' : 'border-border bg-background text-muted-foreground hover:bg-secondary'}`}
      data-testid={n ? 'tagws-card-charges-button-active' : 'tagws-card-charges-button'}>
      <Receipt size={13} />
    </button>
  );
}

export function TagChargesDialog({ item, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (item) setRows(chargeRows(item.extra_charges).length ? chargeRows(item.extra_charges) : [{ name: '', amount: '' }]); }, [item]);
  if (!item) return null;
  const save = async () => {
    if (rows.some((r) => !(r.name || '').trim() && r.amount !== '')) { toast.error('Give every charge a name.'); return; }
    setBusy(true);
    try {
      const { data } = await notesApi.put(`${BASE}/jewellery/${item.id}/charges`, { charges: chargesBody(rows) });
      loadNames(true).catch(() => {});
      toast.success(data.extra_charges.length ? 'Extra charges saved.' : 'Extra charges removed.');
      onSaved?.(item.id, data.extra_charges);
      onClose();
    } catch (e) { toast.error(extractError(e, 'Could not save the extra charges.')); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-3" data-testid="tagws-charges-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={() => !busy && onClose()} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl border border-border bg-card p-4 text-foreground shadow-xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-semibold"><Receipt size={16} className="text-emerald-600" /> Extra charges · <span className="font-mono">{item.jewellery_no}</span></h3>
          <button onClick={() => !busy && onClose()} aria-label="Close" data-testid="tagws-charges-close"><X size={18} /></button>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">Pohai, Hallmarking, Ghisai or any charge. Not printed on the tag — added to the value in priced Excel / PDF. The amount can be left blank and confirmed at price time.</p>
        <ChargesEditor rows={rows} onChange={setRows} />
        <div className="mt-4 flex justify-end gap-2 border-t border-border pt-3">
          <button onClick={onClose} disabled={busy} className="h-9 rounded-lg border border-border px-3 text-sm" data-testid="tagws-charges-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground disabled:opacity-50" data-testid="tagws-charges-save">{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
        </div>
      </div>
    </div>
  );
}
